import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Send, AlertTriangle, FileUp, Upload, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { studentExamApi } from '../../api/examApi';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { checkSubmissionFile } from '../../lib/uploadLimits';
import UploadHint from '../../components/UploadHint';
import PdfExamViewer from '../../components/PdfExamViewer';
import { attendanceBlockedMessage } from '../../components/student/ExamAccessGate';
import ExamCountdown from '../../components/student/ExamCountdown';
import { parseApiError } from '../../lib/apiErrors';

const ATTENDANCE_GATE_DETAIL = 'Attendance not marked present for today';

export default function ExamPortal() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [exam, setExam] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [subjectiveFile, setSubjectiveFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [totalMcq, setTotalMcq] = useState(0);
  const [isUploadCollapsed, setIsUploadCollapsed] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  // Set when the server's attendance gate refuses entry (Step 1). Renders a
  // blocked screen instead of bouncing the student back with a bare toast.
  const [accessBlocked, setAccessBlocked] = useState(null);

  // PDF state — loaded once via authenticated endpoint into a blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfExpired, setPdfExpired] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [hasPdf, setHasPdf] = useState(false);
  const [watermarkTs, setWatermarkTs] = useState(null);
  const blobUrlRef = useRef(null);
  // Guards the one-shot "time's up" toast; re-armed if faculty extends.
  const expiryToastShownRef = useRef(false);

  // Revoke blob URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const fetchPdf = useCallback(async (eid) => {
    try {
      const res = await studentExamApi.getQuestionPdf(eid);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch (err) {
      if (err.response?.status === 403) {
        setPdfExpired(true);
      } else if (err.response?.status !== 404) {
        setPdfLoadError(true);
      }
      // 404 = no PDF uploaded — hasPdf stays false, handled by render
    }
  }, []);

  const startExam = useCallback(async () => {
    try {
      const response = await studentExamApi.startExam(examId);
      const data = response.data;

      setExam(data.exam);
      setAttemptId(data.attempt_id);

      const mcqCount = (data.exam.questions || []).filter(
        (q) => q.question_type === 'mcq' || q.question_type === 'multiple_choice'
      ).length;
      setTotalMcq(mcqCount);

      // Use server-computed remaining seconds — never trust client clock
      setTimeLeft(data.time_remaining_seconds ?? 0);
      setWatermarkTs(new Date().toLocaleString());

      setLoading(false);

      if (data.exam.pdf_url) {
        setHasPdf(true);
        fetchPdf(examId);
      }
    } catch (err) {
      const detail = parseApiError(err, '').message;
      if (err.response?.status === 403 && detail === ATTENDANCE_GATE_DETAIL) {
        // Resolve absent-vs-unmarked so the blocked screen names the actual reason.
        try {
          const { data } = await studentExamApi.getAttendanceToday();
          setAccessBlocked(data.attendance_status || 'unmarked');
        } catch {
          setAccessBlocked('unmarked');
        }
        setLoading(false);
        return;
      }
      toast.error(detail || 'Failed to start exam');
      navigate('/student');
    }
  }, [examId, navigate, fetchPdf]);

  useEffect(() => {
    startExam();
  }, [startExam]);

  // Resync with the server every 60s — picks up faculty-granted extensions
  // without requiring a page reload. Client countdown between polls is
  // display-only; this is what keeps it honest. pdfExpired is read via ref
  // so the interval itself doesn't get torn down and rebuilt on every toggle.
  const pdfExpiredRef = useRef(pdfExpired);
  useEffect(() => {
    pdfExpiredRef.current = pdfExpired;
  }, [pdfExpired]);

  useEffect(() => {
    if (!attemptId) return;

    const poll = setInterval(async () => {
      try {
        const { data } = await studentExamApi.getTimeRemaining(examId);
        const remaining = data.time_remaining_seconds ?? 0;
        setTimeLeft(remaining);
        if (remaining > 0 && pdfExpiredRef.current) {
          // Faculty granted an extension — un-expire and re-arm the toast.
          setPdfExpired(false);
          setPdfLoadError(false);
          expiryToastShownRef.current = false;
          fetchPdf(examId);
        }
      } catch (err) {
        // Transient network error — next poll will retry. Don't disrupt the exam.
      }
    }, 60000);

    return () => clearInterval(poll);
  }, [attemptId, examId, fetchPdf]);

  // Countdown reaching zero hides the question paper immediately — no refresh
  // needed. The server already 403s the PDF route past view_deadline; this is
  // the client catching up in the same instant instead of on the next reload.
  // Do NOT auto-submit: upload stays open until faculty closes submissions.
  // Stable identity: a fresh object literal here would re-trigger PdfPage's
  // render effect on every parent re-render, which is the other half of the
  // flicker (the countdown was the trigger, this was the amplifier).
  const watermark = useMemo(
    () => ({
      name: user?.name || 'Student',
      id: String(user?.id || ''),
      timestamp: watermarkTs || '',
    }),
    [user?.name, user?.id, watermarkTs]
  );

  const handleExpire = useCallback(() => {
    setPdfExpired(true);
    if (!expiryToastShownRef.current) {
      expiryToastShownRef.current = true;
      toast.info("Time's up. The question paper is no longer accessible. You can still upload your answer sheet.");
    }
  }, []);

  const saveAnswer = async (questionNumber, option) => {
    setAnswers((prev) => ({ ...prev, [questionNumber]: option }));
    try {
      await studentExamApi.saveAnswer(attemptId, {
        question_number: questionNumber,
        selected_option: option,
      });
    } catch (err) {
      console.error('Failed to save answer');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileError = checkSubmissionFile(file);
    if (fileError) {
      toast.error(fileError);
      e.target.value = '';
      return;
    }
    setSubjectiveFile(file);
    toast.success('File selected');
  };

  // Upload answer sheet independently from the final submit action
  const handleUploadAnswerSheet = async () => {
    if (!subjectiveFile) {
      toast.error('Select a file first');
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', subjectiveFile);
      await studentExamApi.uploadAnswer(attemptId, formData);
      toast.success('Answer sheet uploaded successfully!');
      setSubjectiveFile(null);
      // Reset the file input by clearing the state; the label will update
    } catch (err) {
      toast.error(parseApiError(err, 'Upload failed').message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await studentExamApi.submitExam(attemptId);
      toast.success('Exam submitted successfully!');
      navigate('/student');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to submit exam').message);
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading exam...</p>
        </div>
      </div>
    );
  }

  // Server refused entry on the attendance gate — show the reason, never the paper.
  if (accessBlocked) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4">
        <div
          className="max-w-md w-full rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-slate-800 p-8 text-center"
          data-testid="exam-blocked-screen"
        >
          <UserCheck size={48} className="mx-auto mb-4 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Exam not available
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {attendanceBlockedMessage(accessBlocked)}
          </p>
          <button
            onClick={() => navigate('/student')}
            className="rounded-lg bg-primary hover:bg-primary-light px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show upload section for subjective/mixed exams, or any exam that has a PDF
  const showUpload =
    exam?.exam_type === 'subjective_only' ||
    exam?.exam_type === 'mixed' ||
    hasPdf ||
    (exam?.questions || []).some((q) => q.question_type === 'subjective');

  // Single source of the Upload Sheet markup, rendered at two different DOM
  // positions depending on breakpoint (mobile: top-level, right after the PDF
  // viewer; desktop: nested in the answer panel, after the question content —
  // see the two call sites below). `variant` keeps element ids/testids unique
  // since both copies exist in the DOM at once (the inactive one is
  // display:none via Tailwind's hidden/lg:hidden, not unmounted).
  const renderUploadSection = (variant) => (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
          <FileUp size={18} />
          Answer Sheet Upload
        </h3>
        <button
          type="button"
          onClick={() => setIsUploadCollapsed((prev) => !prev)}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          data-testid={`toggle-upload-panel-${variant}`}
        >
          {isUploadCollapsed ? (
            <>
              Show <ChevronDown size={14} />
            </>
          ) : (
            <>
              Hide <ChevronUp size={14} />
            </>
          )}
        </button>
      </div>

      {isUploadCollapsed ? (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-3 text-sm text-slate-500 dark:text-slate-400">
          Upload controls are collapsed. Expand this section to select and upload your answer sheet.
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
            Upload your handwritten answers as a PDF. Up to 3 uploads allowed (<UploadHint className="" prefix="" /> each).
          </p>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            id={`subjective-upload-${variant}`}
            data-testid={`subjective-upload-${variant}`}
          />
          <label
            htmlFor={`subjective-upload-${variant}`}
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-primary dark:hover:border-accent-cool transition-colors mb-3"
          >
            <FileUp className="text-slate-400" size={20} />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {subjectiveFile ? subjectiveFile.name : 'Click to select file'}
            </span>
          </label>
          <button
            onClick={handleUploadAnswerSheet}
            disabled={!subjectiveFile || uploadingFile}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid={`upload-answer-btn-${variant}`}
          >
            <Upload size={16} />
            {uploadingFile ? 'Uploading...' : 'Upload Answer Sheet'}
          </button>
        </>
      )}
    </>
  );

  return (
    <div className="h-screen bg-slate-100 dark:bg-slate-900 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between z-20">
        <div>
          <h1 className="font-semibold text-slate-900 dark:text-white">{exam?.title}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{exam?.subject}</p>
        </div>

        <div className="flex items-center gap-4">
          <ExamCountdown seedSeconds={timeLeft} onExpire={handleExpire} />

          <button
            onClick={() => setShowSubmitConfirm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-light text-white rounded-lg font-medium transition-colors"
            data-testid="submit-exam-btn"
          >
            <Send size={18} />
            Submit
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0 flex-col overflow-y-auto lg:flex-row lg:overflow-visible">
        {/* PDF Viewer */}
        <div
          className="shrink-0 h-[50vh] p-4 overflow-auto lg:flex-1 lg:h-auto lg:shrink lg:min-h-0"
          data-testid="pdf-viewer-pane"
        >
          {hasPdf ? (
            pdfExpired ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg p-8 text-center border-2 border-amber-200 dark:border-amber-800">
                <AlertTriangle size={48} className="mx-auto mb-4 text-amber-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Time's Up
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  The question paper is no longer accessible. You can still upload your answer sheet.
                </p>
              </div>
            ) : pdfBlobUrl ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg p-2">
                <PdfExamViewer
                  src={pdfBlobUrl}
                  title="Exam Paper"
                  watermark={watermark}
                  className="min-h-[600px]"
                />
              </div>
            ) : pdfLoadError ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg p-8 text-center">
                <p className="text-red-500">Failed to load question paper. Please refresh.</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Loading question paper...</p>
              </div>
            )
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-lg p-8 text-center">
              <p className="text-slate-500 dark:text-slate-400">
                No PDF question paper uploaded. Please answer the questions in the panel on the right.
              </p>
            </div>
          )}
        </div>

        {/* Answer Sheet Upload — mobile position: directly after the PDF viewer,
            above Question Navigation, so a student scrolls straight to Upload
            instead of past every question first. Hidden at lg+, where the
            desktop copy (nested in the answer panel, below) takes over. */}
        {showUpload && (
          <div
            className="lg:hidden shrink-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-4"
            data-testid="mobile-upload-section"
          >
            {renderUploadSection('mobile')}
          </div>
        )}

        {/* Answer Panel */}
        <div
          className="w-full lg:w-96 bg-white dark:bg-slate-800 border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-700 flex flex-col"
          data-testid="answer-panel"
        >
          {/* Question Navigation */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Question Navigation</h3>
            <div className="flex flex-wrap gap-2">
              {(exam?.questions || []).map((question) => {
                const isAnswered = !!answers[question.question_number];
                const isCurrent = currentQuestion === question.question_number;

                return (
                  <button
                    key={question.question_number}
                    onClick={() => setCurrentQuestion(question.question_number)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      isCurrent
                        ? 'bg-primary text-white'
                        : isAnswered
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    data-testid={`nav-q-${question.question_number}`}
                  >
                    {question.question_number}
                  </button>
                );
              })}
            </div>
            {totalMcq > 0 && (
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-emerald-100 dark:bg-emerald-900/30 rounded" /> Answered
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-slate-100 dark:bg-slate-700 rounded" /> Not Answered
                </span>
              </div>
            )}
          </div>

          {/* Current Question */}
          <div className="flex-1 min-h-0 p-4 overflow-auto">
            {exam?.questions && exam.questions.length > 0 && (
              <>
                {(() => {
                  const currentQ = exam.questions.find((q) => q.question_number === currentQuestion);
                  if (!currentQ) return null;

                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-slate-900 dark:text-white">
                          Question {currentQ.question_number}
                        </h3>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          {currentQ.marks} mark(s)
                        </span>
                      </div>

                      <div className="mb-4">
                        <p className="text-slate-900 dark:text-white mb-4">{currentQ.question_text}</p>
                        {currentQ.image_url && (
                          <div className="mb-4">
                            <img
                              src={currentQ.image_url}
                              alt="Question diagram"
                              className="max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-600"
                            />
                          </div>
                        )}
                      </div>

                      {(currentQ.question_type === 'mcq' || currentQ.question_type === 'multiple_choice') &&
                      currentQ.options ? (
                        <div className="space-y-2">
                          {currentQ.options.map((option, index) => {
                            const optionLetter = String.fromCharCode(65 + index);
                            const isSelected = answers[currentQuestion]
                              ?.split(',')
                              .includes(index.toString());

                            return (
                              <button
                                key={index}
                                onClick={() => {
                                  if (currentQ.question_type === 'mcq') {
                                    saveAnswer(currentQuestion, index.toString());
                                  } else {
                                    const currentAnswers =
                                      answers[currentQuestion]?.split(',') || [];
                                    const newAnswers = isSelected
                                      ? currentAnswers.filter((a) => a !== index.toString())
                                      : [...currentAnswers, index.toString()];
                                    saveAnswer(currentQuestion, newAnswers.join(','));
                                  }
                                }}
                                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                  isSelected
                                    ? 'border-primary bg-primary/10 text-primary dark:border-accent-cool dark:bg-accent-cool/10 dark:text-accent-cool'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                }`}
                                data-testid={`option-${optionLetter}`}
                              >
                                <span className="font-medium mr-3">{optionLetter}.</span>
                                <span className="text-slate-600 dark:text-slate-400">{option}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : currentQ.question_type === 'subjective' ? (
                        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                          <FileUp size={48} className="mx-auto mb-4 opacity-50" />
                          <p>This is a subjective question.</p>
                          <p className="text-sm">Upload your answer sheet below.</p>
                        </div>
                      ) : null}

                      <div className="flex items-center justify-between mt-6">
                        <button
                          onClick={() => {
                            const idx = exam.questions.findIndex(
                              (q) => q.question_number === currentQuestion
                            );
                            if (idx > 0) setCurrentQuestion(exam.questions[idx - 1].question_number);
                          }}
                          disabled={
                            exam.questions.findIndex((q) => q.question_number === currentQuestion) === 0
                          }
                          className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-50"
                        >
                          <ChevronLeft size={18} /> Previous
                        </button>
                        <button
                          onClick={() => {
                            const idx = exam.questions.findIndex(
                              (q) => q.question_number === currentQuestion
                            );
                            if (idx < exam.questions.length - 1)
                              setCurrentQuestion(exam.questions[idx + 1].question_number);
                          }}
                          disabled={
                            exam.questions.findIndex((q) => q.question_number === currentQuestion) ===
                            exam.questions.length - 1
                          }
                          className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-50"
                        >
                          Next <ChevronRight size={18} />
                        </button>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {/* Answer Sheet Upload — desktop position (unchanged from before this
                session's mobile reorder). Below lg this copy is display:none; the
                mobile copy renders instead, directly after the PDF viewer. */}
            {showUpload && (
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 hidden lg:block">
                {renderUploadSection('desktop')}
              </div>
            )}
          </div>

          {/* Summary */}
          {totalMcq > 0 && (
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
              <div className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium text-slate-900 dark:text-white">
                  {Object.keys(answers).length}
                </span>{' '}
                of{' '}
                <span className="font-medium text-slate-900 dark:text-white">{totalMcq}</span> answered
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 animate-fade-in">
            <div className="flex items-center gap-3 text-amber-600 mb-4">
              <AlertTriangle size={24} />
              <h3 className="text-lg font-semibold">Submit Exam?</h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-2">
              {totalMcq > 0 && (
                <>
                  You have answered {Object.keys(answers).length} out of {totalMcq} MCQ questions.
                  {Object.keys(answers).length < totalMcq && (
                    <span className="block mt-1 text-amber-600">
                      {totalMcq - Object.keys(answers).length} question(s) unanswered!
                    </span>
                  )}
                </>
              )}
            </p>
            {showUpload && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Make sure you have uploaded your answer sheet before submitting.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Continue Exam
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-primary hover:bg-primary-light text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                data-testid="confirm-submit"
              >
                {submitting ? 'Submitting...' : 'Submit Exam'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
